import { APP_CONFIG } from "@/config/app-config";
import {
  AppError,
  InternalError,
  isPublicError,
  PublicError,
} from "@/domain/errors/common";
import {
  BadRequestNetworkError,
  createNetworkError,
  ForbiddenNetworkError,
  HandledStatusCode,
  InternalServerNetworkError,
  NotFoundNetworkError,
  TaggedNetworkError,
  UnauthorizedNetworkError,
} from "@/domain/errors/network";
import { AxiosError, isAxiosError } from "axios";

function handleNetworkError(
  statusCode: unknown,
  response: unknown,
  notFoundError?: PublicError | InternalError
): PublicError | InternalError {
  switch (statusCode) {
    case 400:
      return new BadRequestNetworkError();
    case 401:
      return new UnauthorizedNetworkError();
    case 403:
      return new ForbiddenNetworkError();
    case 404:
      return notFoundError || new NotFoundNetworkError();
    case 500:
      return new InternalServerNetworkError();
    default:
      return new InternalError(
        `Error de red desconocido, codigo de estado: ${statusCode}, error: ${JSON.stringify(response)}`
      );
  }
}

// Overload 1: For calls without a custom Axios error handler
function handleTaggedNetworkErrors(
  error: unknown,
  customMessages?: Partial<Record<HandledStatusCode, string>>
): TaggedNetworkError;

// Overload 2: For calls with a custom Axios error handler
function handleTaggedNetworkErrors<T extends AppError>(
  error: unknown,
  customMessages: Partial<Record<HandledStatusCode, string>> | undefined,
  customAxiosErrorHandler: (error: AxiosError) => T | null
): TaggedNetworkError | T;

// Implementation
function handleTaggedNetworkErrors<T extends AppError>(
  error: unknown,
  customMessages?: Partial<Record<HandledStatusCode, string>>,
  customAxiosErrorHandler?: (error: AxiosError) => T | null
): TaggedNetworkError | T {
  const DEFAULT_ERROR = createNetworkError(999, customMessages);
  if (!isAxiosError(error)) {
    return DEFAULT_ERROR;
  }
  if (!error.response?.status) {
    return DEFAULT_ERROR;
  }

  if (customAxiosErrorHandler) {
    const customError = customAxiosErrorHandler(error);
    if (customError) return customError;
  }

  return createNetworkError(error.response.status, customMessages);
}

// Overload 1: For calls without a custom Axios error handler
function repositoryNetworkErrorHandler<T extends AppError>(
  error: unknown,
  extras: {
    customAxiosErrorHandler: (error: AxiosError) => T | null;
    customMessages?: Partial<Record<HandledStatusCode, string>>;
  }
): TaggedNetworkError | T;

// Overload 2: For calls without a custom Axios error handler
function repositoryNetworkErrorHandler(
  error: unknown,
  extras?: {
    customAxiosErrorHandler?: never;
    customMessages?: Partial<Record<HandledStatusCode, string>>;
  }
): TaggedNetworkError;

// Implementation
function repositoryNetworkErrorHandler<T extends AppError>(
  error: unknown,
  extras?: {
    customAxiosErrorHandler?: (error: AxiosError) => T | null;
    customMessages?: Partial<Record<HandledStatusCode, string>>;
  }
): TaggedNetworkError | T {
  if (APP_CONFIG.getCurrentEnvironment() === "development") {
    console.error("Repository network error: ", error);
  }

  if (extras?.customAxiosErrorHandler) {
    return handleTaggedNetworkErrors(
      error,
      extras.customMessages,
      extras.customAxiosErrorHandler
    );
  }

  return handleTaggedNetworkErrors(error, extras?.customMessages);
}

function repositoryErrorHandler(
  error: unknown,
  fallbackError: InternalError | PublicError,
  notFoundError?: PublicError | InternalError,
  customHandler?: (error: unknown) => InternalError | PublicError | undefined
): InternalError | PublicError {
  if (APP_CONFIG.getCurrentEnvironment() === "development") {
    console.error("Repository error: ", error);
  }

  if (customHandler) {
    const customError = customHandler(error);
    if (customError) return customError;
  }

  if (isPublicError(error)) return error;

  if (isAxiosError(error)) {
    const msg = error.response?.data?.message || error.response?.data?.error;
    const statusCode = error.response?.status;

    if (statusCode == 404 && notFoundError) {
      return handleNetworkError(statusCode, msg, notFoundError);
    }

    if (msg != undefined && msg != "") {
      return new InternalError(msg);
    }

    return handleNetworkError(statusCode, error.response?.data, notFoundError);
  }

  return fallbackError;
}

export { repositoryErrorHandler, repositoryNetworkErrorHandler };
